import React, { Fragment, useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import upi from "../images/upi.png";
import ssimg from "../images/SS img.png";
import slogan from "../images/slogan.png";

import sloganMob from "../images/sloganMob.png";
import "./DonateForm.css";
import ForeignDonationForm from "./ForeignDonationForm";
import axios from "axios";
import { cashfree } from "../util";
import { Box, Typography, Collapse } from "@mui/material";
import { getApiUrl, RAZORPAY_KEY, API_BASE_URL } from "../config";
import DonationStatusModal from "./DonationStatusModal";

const POPULAR_DEDUCTION_DAYS = [
  { day: "1", label: "1st", tag: "Salary" },
  { day: "5", label: "5th", tag: "Popular" },
  { day: "10", label: "10th", tag: "" },
  { day: "15", label: "15th", tag: "" },
  { day: "20", label: "20th", tag: "" },
  { day: "25", label: "25th", tag: "" },
  { day: "28", label: "28th", tag: "Safe" },
];

const getOrdinalSuffix = (day) => {
  const d = parseInt(day, 10);
  if (isNaN(d)) return day;
  const s = ["th", "st", "nd", "rd"];
  const v = d % 100;
  return d + (s[(v - 20) % 10] || s[v] || s[0]);
};

const getNextDeductionDateText = (day) => {
  const targetDay = parseInt(day, 10) || new Date().getDate();
  const now = new Date();
  const currentDay = now.getDate();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();

  let nextDate;
  if (currentDay < targetDay) {
    nextDate = new Date(currentYear, currentMonth, targetDay);
  } else {
    nextDate = new Date(currentYear, currentMonth + 1, targetDay);
  }

  const options = { day: "numeric", month: "short", year: "numeric" };
  return nextDate.toLocaleDateString("en-IN", options);
};

const DonateForm = () => {
  // React Hook Form setup
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset
  } = useForm({
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      country: "India",
      city: "",
      state: "",
      address1: "",
      address2: "",
      postalCode: "",
      donationAmount: "",
      donationFrequency: "Monthly",
      deductionDay: String(new Date().getDate()),
      currency: "INR",
      honorName: "",
      honorEmail: "",
    },
    mode: "onChange" // Validate on change for better UX
  });

  // Watch form values for UI updates
  const watchedValues = watch();

  const [paymentOption, setPaymentOption] = useState("monthly");
  const [isChecked, setIsChecked] = useState(false);
  const [selectedOption, setSelectedOption] = useState("honourOf");
  const [loading, setLoading] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState("");
  const [selectedDeductionDay, setSelectedDeductionDay] = useState(String(new Date().getDate()));
  const [citizenType, setCitizenType] = useState("indian"); // New state for citizen type

  const [sessionId, setSessionId] = useState("");
  const [orderId, setOrderId] = useState("");
  const [expanded, setExpanded] = useState([false, false, false, false, false]);
  const [statusModal, setStatusModal] = useState({
    open: false,
    type: "info",
    title: "",
    message: "",
    details: null,
    primaryButtonText: "Close",
    onPrimaryAction: null,
  });

  const closeStatusModal = () => {
    setStatusModal((prev) => ({ ...prev, open: false }));
  };

  const isMobile = window.innerWidth <= 1168;

  const handleToggle = (index) => {
    setExpanded((prevExpanded) =>
      prevExpanded.map((value, i) => (i === index ? !value : value))
    );
  };

  // const navigate = useNavigate();

  const handlePaymentButtonClick = (option) => {
    setPaymentOption(option);
    setValue("donationFrequency", option === "monthly" ? "Monthly" : "One Time");
  };

  const handleButtonClick = (option) => {
    setSelectedOption(option);
  };

  const handleCheckboxChange = (event) => {
    setIsChecked(event.target.checked);
  };

  const handleCitizenTypeChange = (type) => {
    setCitizenType(type);
  };

  useEffect(() => {
    const savedFormData = sessionStorage.getItem("donationFormData");
    if (savedFormData) {
      const data = JSON.parse(savedFormData);
      reset(data); // Use reset to populate form with saved data
      if (data.donationFrequency === "One Time") {
        setPaymentOption("oneTime");
      } else {
        setPaymentOption("monthly");
      }
      if (data.deductionDay) {
        setSelectedDeductionDay(String(data.deductionDay));
      }
    }
  }, [reset]);

  useEffect(() => {
    sessionStorage.setItem("donationFormData", JSON.stringify(watchedValues));
  }, [watchedValues]);

  const handlePredefinedAmountClick = (amount) => {
    setValue("donationAmount", amount);
    setSelectedAmount(amount);
  };

  const handleDeductionDaySelect = (day) => {
    const dayStr = String(day);
    setSelectedDeductionDay(dayStr);
    setValue("deductionDay", dayStr, { shouldValidate: true, shouldDirty: true });
  };

  const handlePayment = async (data) => {
    setLoading(true);

    try {
      await axios.post(
        getApiUrl("/api/form/create"),
        data
      );

      if (paymentOption === "oneTime") {
        const requestData = {
          customer_details: {
            customer_email: data.email,
            customer_phone: data.phone,
            customer_name: data.firstName + " " + data.lastName,
          },
          order_amount: data.donationAmount,
          order_currency: data.currency,
          honor_name: data.honorName,
          honor_email: data.honorEmail,
        };
        const oneTimeResponse = await axios.post(
          getApiUrl("/api/oneTime/createOrder"),
          requestData
        );

        sessionStorage.setItem("orderId", oneTimeResponse.data.order_id);
        sessionStorage.setItem("customer_name", data.firstName + " " + data.lastName);
        sessionStorage.setItem("customer_email", data.email);
        sessionStorage.setItem("order_amount", data.donationAmount);
        sessionStorage.setItem("order_currency", data.currency);
        sessionStorage.setItem("honor_name", data.honorName);
        sessionStorage.setItem("honor_email", data.honorEmail);

        setSessionId(oneTimeResponse.data.payment_session_id);
        setOrderId(oneTimeResponse.data.order_id);
        setLoading(false);

        // Reset form after successful submission
        reset();
        setSelectedAmount("");
        setIsChecked(false);

      } else if (paymentOption === "monthly") {
        const todayDayStr = String(new Date().getDate());
        const deductionDayVal = data.deductionDay || selectedDeductionDay || todayDayStr;

        const requestData = {
          order_amount: data.donationAmount,
          order_currency: data.currency,
          honor_name: data.honorName,
          honor_email: data.honorEmail,
          deductionDay: deductionDayVal,
          customer_details: {
            customer_email: data.email,
            customer_phone: data.phone,
            customer_name: data.firstName + " " + data.lastName,
          },
        };

        try {
          const subscriptionResponse = await axios.post(
            getApiUrl("/api/subscription/createSubscriptionRazorpay"),
            requestData
          );

          sessionStorage.setItem("subscriptionId", subscriptionResponse.data.id);
          sessionStorage.setItem("customer_name", data.firstName + " " + data.lastName);
          sessionStorage.setItem("customer_email", data.email);
          sessionStorage.setItem("order_amount", data.donationAmount);
          sessionStorage.setItem("order_currency", data.currency);
          sessionStorage.setItem("honor_name", data.honorName);
          sessionStorage.setItem("honor_email", data.honorEmail);
          sessionStorage.setItem("deductionDay", deductionDayVal);

          setSessionId(subscriptionResponse.data.subscription_session_id);
          setOrderId(subscriptionResponse.data.id);
          setLoading(false);

          const options = {
            key: RAZORPAY_KEY,
            subscription_id: subscriptionResponse.data.id,
            name: 'Sood Charity Foundation',
            description: 'Monthly Subscription',
            handler: function (response) {
              console.log(response);
              setStatusModal({
                open: true,
                type: "success",
                title: "Subscription Started!",
                message: "Your recurring donation has been successfully initiated. Thank you for your kindness and generous support towards the foundation's initiatives.",
                details: {
                  Amount: `₹${data.donationAmount}`,
                  Frequency: "Monthly",
                  DeductionDate: `${getOrdinalSuffix(deductionDayVal)} of every month`,
                  SubscriptionID: subscriptionResponse.data.id,
                },
                primaryButtonText: "View Receipt / Continue",
                onPrimaryAction: () => {
                  window.location.href = `thankYou/${subscriptionResponse.data.id}`;
                },
              });
            },
            prefill: {
              name: data.firstName + " " + data.lastName,
              email: data.email,
              contact: data.phone,
            },
            theme: {
              color: '#F37254',
            },
            method: {
              netbanking: true,
              card: true,
              upi: true,
              wallet: true
            }
          };

          const rzp = new window.Razorpay(options);
          rzp.open();
        } catch (error) {
          console.error('Error creating subscription:', error);
          setLoading(false);
          setStatusModal({
            open: true,
            type: "error",
            title: "Subscription Setup Incomplete",
            message: error?.response?.data?.message || "We could not set up your monthly subscription. Please check your network connection or payment details and try again.",
            primaryButtonText: "Try Again",
            onPrimaryAction: closeStatusModal,
          });
        }

        // Reset form after successful submission
        reset();
        setSelectedAmount("");
        setIsChecked(false);
      }
    } catch (error) {
      console.error("Payment error: ", error);
      setLoading(false);
      setStatusModal({
        open: true,
        type: "error",
        title: "Payment Initialization Failed",
        message: "An error occurred while preparing your donation request. Please try again in a few moments.",
        primaryButtonText: "Close",
        onPrimaryAction: closeStatusModal,
      });
    }
  };
  useEffect(() => {
    if (sessionId) {
      // let checkoutOptions = {
      //   paymentSessionId: sessionId,
      //   orderId:orderId,
      //   returnUrl:`https://donate.soodcharityfoundation.org/thankYou/${orderId}`,
      // };
      let checkoutOptions = {
        returnUrl: `${API_BASE_URL}/thankYou/${orderId}`,
        // returnUrl: `http://localhost:3000/thankYou/${orderId}`,
      };

      if (paymentOption === "oneTime") {
        checkoutOptions.paymentSessionId = sessionId;

        cashfree.checkout(checkoutOptions).then(function (result) {
          if (result.error) {
            setStatusModal({
              open: true,
              type: "error",
              title: "Gateway Checkout Error",
              message: result.error.message || "Payment gateway encountered an error. Please try again.",
              primaryButtonText: "Close",
              onPrimaryAction: closeStatusModal,
            });
          }
          if (result.redirect) {
            console.log("Redirection");
          }
        });
      }
    }
  }, [sessionId, paymentOption, orderId]);
  const faqData = [
    {
      question: "Q. How can I donate in honor or memory of someone?",
      answer: "You can dedicate your donation in honor or memory of a loved one by selecting the required option during the donation process. We’ll send a personalized notification to the honoree or their family if an email is provided."
    },
    {
      question: "Q. Is my donation secure?",
      answer: "Yes, we use secure and encrypted payment gateways to ensure that your personal and financial information is protected during the donation process."
    },
    {
      question: "Q. Is my donation tax-deductible?",
      answer: "Yes, donations are tax-deductible to the fullest extent permitted by law. You'll receive a confirmation receipt for tax purposes."
    },
    {
      question: "Q. How will my donation be used?",
      answer: "Your donation will be used to support various programs and initiatives that align with our mission."
    },
    {
      question: "Q. Can I cancel or change my recurring donation?",
      answer: (
        <>
          You can cancel or adjust your recurring donation at any time. Please contact us at{" "}
          <a href="mailto:contact@soodcharityfoundation.org" style={{ color: 'white' }}>contact@soodcharityfoundation.org</a>{" "}
          for assistance with changes.
        </>
      )
    }
  ];

  return (
    <Fragment>
      <h1
        className="heading supportus"
        style={{
          textAlign: "center",
          display: "block",
          width: "auto",
          fontSize: "64px",

          fontWeight: "600",
          marginBottom: "5%",
        }}
      >
        <span style={{ color: "#00345B" }}>Support </span>
        <span style={{ color: "#00345B" }}>Us</span>
      </h1>

      <div className="hero_section container" style={{ width: '100%' }}>
        <div className="description" style={{ marginLeft: "0%", marginRight: "2%", width: "100%" }}>
          <h2
            style={{
              textAlign: "left",
              display: "block",
              //width: "40%",
              fontSize: "44px",
              fontWeight: "600",
              color: "#00345B",
            }}
          >
            Message from Founder
          </h2>
          <h2
            style={{
              textAlign: "left",
              display: "block",
              //width: "40%",
              fontSize: "44px",
              fontWeight: "600",
              color: "#00345B",
            }}
          >
            Sonu Sood
          </h2>
          <p
            style={{
              marginTop: "2%",
              //  width:"40%"
            }}
          >
            <span
              style={{ color: "#888888", fontSize: "24px", fontWeight: "400" }}
            >
              “I believe that each one of us has the power to change the world,
              one act of kindness at a time. Whether it's providing food to the
              hungry, shelter to the homeless, or education to the
              underprivileged, every little bit counts.
            </span>
            <span
              style={{ color: "#00345B", fontSize: "24px", fontWeight: "400" }}
            >
              {" "}I've seen firsthand the impact that even the smallest gesture of
              kindness can have on someone's life.
            </span>
            <span
              style={{ color: "#888888", fontSize: "24px", fontWeight: "400" }}
            >
              {" "}It's like a ripple effect it starts small, but it grows and
              spreads, touching the lives of countless others along the way.”
            </span>
          </p>
          <div className="image_style">
            {citizenType === "indian" ? (
              <img
                className="img_Qe QR-Desktop"
                src={upi}
                alt="upi"
                style={{ marginLeft: "5rem" }}
                width="60%"
                height="auto"
              />
            ) : (
              <div className="QR-Desktop qr-placeholder-desktop">
                <div className="qr-placeholder-icon">
                  🚧
                </div>
                <h4 className="qr-placeholder-title">
                  Digital Transactions Coming Soon
                </h4>
                <p className="qr-placeholder-text">
                  Digital payment options for foreign nationals will be available soon.
                  Please use the donation form to proceed with your contribution.
                </p>
              </div>
            )}
          </div>
        </div>
        <div className="form_section">
          <div
            className="form-container"
            style={{
              boxShadow: "0px 0px 12px 0px rgba(20, 58, 162, 0.1)",
              borderRadius: "20px",
              padding: "1.5rem",
              background: "rgba(255, 255, 255, 1)",
              width: "100%",
              height: "auto",
              marginTop: "0",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Policy Documents Reference */}
            <div style={{
              backgroundColor: "#f8f9fa",
              border: "1px solid #dee2e6",
              borderRadius: "8px",
              padding: "12px",
              margin: "0 0 20px 0",
              textAlign: "center"
            }}>
              <p style={{
                fontSize: "14px",
                color: "#495057",
                margin: 0,
                lineHeight: "1.5"
              }}>
                <strong>📄 Not sure how to donate?</strong> Please refer to these documents:{" "}
                <a
                  href="/policy/Donor_Self_Assessment_Checklist.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "#00345B",
                    textDecoration: "underline",
                    fontWeight: "500"
                  }}
                >
                  Donor Self Assessment Checklist
                </a>
                {" "}and{" "}
                <a
                  href="/policy/FCRA_Compliance_and_Privacy_Policy.docx"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "#00345B",
                    textDecoration: "underline",
                    fontWeight: "500"
                  }}
                >
                  FCRA Compliance & Privacy Policy
                </a>
              </p>
            </div>

            {/* Citizen Type Toggle */}
            <div className="donor-container" style={{ marginBottom: "30px" }}>
              <div className="donor-header">
                <h3 className="donor-title">
                  Select Donor Type
                </h3>
                <p className="donor-subtitle">
                  Choose your citizenship status to proceed
                </p>
              </div>

              <div className="donor-selector-wrapper">
                {/* Sliding background indicator */}
                <div
                  className={`donor-indicator ${citizenType}`}
                  id="donorIndicator"
                ></div>

                <div className="donor-buttons-container">
                  <button
                    type="button"
                    className={`donor-button ${citizenType === "indian" ? "active" : "inactive"}`}
                    onClick={() => handleCitizenTypeChange("indian")}
                  >
                    {/* Users Icon
                    <svg 
                      className={`donor-icon ${citizenType === "indian" ? "indian-active" : "indian-inactive"}`}
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth="2" 
                        d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                      ></path>
                    </svg> */}
                    <span className="donor-button-text">
                      Indian Citizen
                    </span>
                  </button>

                  <button
                    type="button"
                    className={`donor-button ${citizenType === "foreign" ? "active" : "inactive"}`}
                    onClick={() => handleCitizenTypeChange("foreign")}
                  >
                    {/* Globe Icon
                    <svg 
                      className={`donor-icon ${citizenType === "foreign" ? "foreign-active" : "foreign-inactive"}`}
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth="2" 
                        d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      ></path>
                    </svg> */}
                    <span className="donor-button-text">
                      Foreign National
                    </span>
                  </button>
                </div>
              </div>

              {/* Selected option display */}
              <div className="donor-status">
                <div className="donor-status-badge">
                  <div className="donor-pulse-dot"></div>
                  <span className="donor-status-text">
                    Selected: {citizenType === "indian" ? "Indian Citizen" : "Foreign National"}
                  </span>
                </div>
              </div>
            </div>

            {citizenType === "indian" ? (
              <form onSubmit={handleSubmit(handlePayment)}>
                <h3 className="frequency-title">How frequently would you like to donate?</h3>
                <div className="frequency-buttons-wrapper mb-4 mt-3">
                  {/* Monthly Option (Default) */}
                  <button
                    type="button"
                    className={`frequency-btn ${paymentOption === "monthly" ? "active" : "inactive"
                      }`}
                    onClick={() => handlePaymentButtonClick("monthly")}
                  >
                    <div className="frequency-btn-content">
                      <div className="frequency-radio-circle">
                        {paymentOption === "monthly" && <div className="frequency-radio-dot" />}
                      </div>
                      <svg
                        className="frequency-icon"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67" />
                      </svg>
                      <div className="frequency-btn-text">
                        <span className="frequency-main-label">Monthly</span>
                        <span className="frequency-sub-label">Recurring support</span>
                      </div>
                    </div>
                    <span className="frequency-badge">Recommended</span>
                  </button>

                  {/* One Time Option */}
                  <button
                    type="button"
                    className={`frequency-btn ${paymentOption === "oneTime" ? "active" : "inactive"
                      }`}
                    onClick={() => handlePaymentButtonClick("oneTime")}
                  >
                    <div className="frequency-btn-content">
                      <div className="frequency-radio-circle">
                        {paymentOption === "oneTime" && <div className="frequency-radio-dot" />}
                      </div>
                      <svg
                        className="frequency-icon"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                      </svg>
                      <div className="frequency-btn-text">
                        <span className="frequency-main-label">One Time</span>
                        <span className="frequency-sub-label">Single contribution</span>
                      </div>
                    </div>
                  </button>
                </div>

                {/* Monthly Donation Encouragement & Assurance Box */}
                {paymentOption === "monthly" && (
                  <div className="monthly-assurance-card mb-4">
                    <div className="monthly-assurance-header">
                      <div className="monthly-assurance-icon-wrap">
                        <svg
                          className="monthly-assurance-icon"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                          <path d="m9 12 2 2 4-4" />
                        </svg>
                      </div>
                      <div className="monthly-assurance-heading-text">
                        <h4 className="monthly-assurance-title">Build a Habit of Giving • 100% Flexible</h4>
                        <p className="monthly-assurance-subtitle">
                          Start a consistent contribution towards society. You stay in full control.
                        </p>
                      </div>
                    </div>

                    <div className="monthly-assurance-body">
                      <div className="monthly-assurance-points">
                        <div className="monthly-point-item">
                          <span className="point-icon">🔄</span>
                          <span className="point-text">
                            <strong>Pause or cancel anytime:</strong> You’re always in control of your monthly donation. Pause or cancel whenever you wish, with no hassle or obligation.
                          </span>
                        </div>
                        <div className="monthly-point-item">
                          <span className="point-icon">🌱</span>
                          <span className="point-text">
                            <strong>A meaningful monthly habit:</strong> Even modest recurring support creates lasting, sustainable impact for patients and underprivileged families.
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Preferred Monthly Deduction Date Selector */}
                {paymentOption === "monthly" && (
                  <div className="deduction-date-card mb-4">
                    <div className="deduction-date-header">
                      <div className="deduction-date-icon-wrap">
                        <svg
                          className="deduction-date-icon"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                          <line x1="16" y1="2" x2="16" y2="6" />
                          <line x1="8" y1="2" x2="8" y2="6" />
                          <line x1="3" y1="10" x2="21" y2="10" />
                        </svg>
                      </div>
                      <div className="deduction-date-heading-text">
                        <h4 className="deduction-date-title">Preferred Monthly Deduction Date</h4>
                        <p className="deduction-date-subtitle">
                          Choose a date that fits your monthly budget or salary schedule
                        </p>
                      </div>
                    </div>

                    <div className="deduction-date-body">
                      <div className="deduction-pills-label">Popular monthly dates:</div>
                      <div className="deduction-pills-grid">
                        {POPULAR_DEDUCTION_DAYS.map(({ day, label, tag }) => {
                          const isSelected = String(selectedDeductionDay) === String(day);
                          return (
                            <button
                              key={day}
                              type="button"
                              className={`deduction-pill-btn ${isSelected ? "active" : ""}`}
                              onClick={() => handleDeductionDaySelect(day)}
                            >
                              <span className="deduction-pill-day">{label}</span>
                              {tag ? <span className="deduction-pill-tag">{tag}</span> : null}
                            </button>
                          );
                        })}
                      </div>

                      <div className="deduction-custom-row">
                        <label htmlFor="customDeductionDay" className="deduction-custom-label">
                          Or pick any day (1st – 31st):
                        </label>
                        <div className="deduction-select-wrapper">
                          <select
                            id="customDeductionDay"
                            className="form-select deduction-select"
                            value={selectedDeductionDay}
                            onChange={(e) => handleDeductionDaySelect(e.target.value)}
                          >
                            {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                              <option key={d} value={String(d)}>
                                {getOrdinalSuffix(d)} of every month {d > 28 ? "(or last day of month)" : ""}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="deduction-preview-banner">
                        <div className="deduction-preview-icon">🗓️</div>
                        <div className="deduction-preview-text">
                          <div className="deduction-preview-main">
                            Monthly deduction: <strong>Every {getOrdinalSuffix(selectedDeductionDay)} of the month</strong>
                          </div>
                          <div className="deduction-preview-sub">
                            Next scheduled debit: <strong>{getNextDeductionDateText(selectedDeductionDay)}</strong>. An email notification is sent before every cycle.
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {/* ////////////////////////////////////////////// */}
                {/* <div className="row mb-3 mt-2">
            <select
              id="inputCurrency"
              className="form-select"
              value={formData.currency}
              onChange={handleChange}
              name="currency"
            >
              <option defaultValue>Select Currency</option>
              <option value="INR">Indian Rupee (INR)</option>
              <option value="USD">US Dollar (USD)</option>
              <option value="BDT">Bangladesh Taka (BDT)</option>
              <option value="GBP">Pound Sterling (GBP)</option>
              <option value="AED">UAE Dirham (AED)</option>
              <option value="AUD">Australian Dollar (AUD)</option>
              <option value="BHD">Bahraini Dinar (BHD)</option>
              <option value="CAD">Canadian Dollar (CAD)</option>
              <option value="CHF">Swiss Franc (CHF)</option>
              <option value="DKK">Danish Krone (DKK)</option>
              <option value="EUR">Euro (EUR)</option>
              <option value="HKD">Hong Kong Dollar (HKD)</option>
              <option value="JPY">Japanese Yen (JPY)</option>
              <option value="KES">Kenya Shiling (KES)</option>
              <option value="KWD">Kuwaiti Dinar (KWD)</option>
              <option value="LKR">Srilanka Rupee (LKR)</option>
              <option value="MUR">Mauritius Rupee (MUR)</option>
              <option value="MYR">Malaysian Ringgit (MYR)</option>
              <option value="NOK">Norwegian Krone (NOK)</option>
              <option value="NPR">Nepalese Rupee (NPR)</option>
              <option value="NZD">New Zealand Dollar (NZD)</option>
              <option value="OMR">Rial Omani (OMR)</option>
              <option value="QAR">Qatari Rial (QAR)</option>
              <option value="SAR">Saudi Riyal (SAR)</option>
              <option value="SEK">Swedish Krona (SEK)</option>
              <option value="SGD">Singapore Dollar (SGD)</option>
              <option value="THB">Thai Baht (THB)</option>
              <option value="ZAR">South African Rand (ZAR)</option>
            </select>
            {errors.currency && (
              <span className="error-text">{errors.currency}</span>
            )}
          </div> */}
                {/* ////////////////////////////////////////////// */}

                <div className="row mb-3 mt-2">
                  {/* First Row */}
                  <div className="col-4 d-flex justify-content-center mb-2">
                    <button
                      type="button"
                      className={`btn w-100 ${selectedAmount === "500" ? "selected-btn" : ""
                        }`}
                      style={{
                        // color: "#9ca0a1",
                        color: selectedAmount === "500" ? "white" : "#9ca0a1",
                        border: "1px solid #e0e0e0",
                        borderRadius: "8px",
                        padding: "10px 0",
                        whiteSpace: "nowrap",
                        backgroundColor:
                          selectedAmount === "500" ? "#09AACC" : "transparent", // Highlight color
                      }}
                      onClick={() => handlePredefinedAmountClick("500")}
                    >
                      ₹500
                    </button>
                  </div>
                  <div className="col-4 d-flex justify-content-center mb-2">
                    <button
                      type="button"
                      className={`btn w-100 ${selectedAmount === "1000" ? "selected-btn" : ""
                        }`}
                      style={{
                        // color: "#9ca0a1",
                        color: selectedAmount === "1000" ? "white" : "#9ca0a1",
                        border: "1px solid #e0e0e0",
                        borderRadius: "8px",
                        padding: "10px 0",
                        whiteSpace: "nowrap",
                        backgroundColor:
                          selectedAmount === "1000" ? "#09AACC" : "transparent", // Highlight color
                      }}
                      onClick={() => handlePredefinedAmountClick("1000")}
                    >
                      ₹1000
                    </button>
                  </div>
                  <div className="col-4 d-flex justify-content-center mb-2">
                    <button
                      type="button"
                      className={`btn w-100 ${selectedAmount === "5000" ? "selected-btn" : ""
                        }`}
                      style={{
                        // color: "#9ca0a1",
                        color: selectedAmount === "5000" ? "white" : "#9ca0a1",
                        border: "1px solid #e0e0e0",
                        borderRadius: "8px",
                        padding: "10px 0",
                        whiteSpace: "nowrap",
                        backgroundColor:
                          selectedAmount === "5000" ? "#09AACC" : "transparent", // Highlight color
                      }}
                      onClick={() => handlePredefinedAmountClick("5000")}
                    >
                      ₹5000
                    </button>
                  </div>
                </div>

                {/* Second Row */}
                <div className="second-row row mb-3">
                  <div className="col-4 d-flex justify-content-center mb-2">
                    <button
                      type="button"
                      className={`btn w-100 ${selectedAmount === "10000" ? "selected-btn" : ""
                        }`}
                      style={{
                        // color: "#9ca0a1",
                        color: selectedAmount === "10000" ? "white" : "#9ca0a1",
                        border: "1px solid #e0e0e0",
                        borderRadius: "8px",
                        padding: "10px 0",
                        whiteSpace: "nowrap",
                        backgroundColor:
                          selectedAmount === "10000" ? "#09AACC" : "transparent", // Highlight color
                      }}
                      onClick={() => handlePredefinedAmountClick("10000")}
                    >
                      ₹10,000
                    </button>
                  </div>
                  <div className="col-4 d-flex justify-content-center mb-2">
                    <button
                      type="button"
                      className={`btn w-100 ${selectedAmount === "15000" ? "selected-btn" : ""
                        }`}
                      style={{
                        // color: "#9ca0a1",
                        color: selectedAmount === "15000" ? "white" : "#9ca0a1",
                        border: "1px solid #e0e0e0",
                        borderRadius: "8px",
                        padding: "10px 0",
                        whiteSpace: "nowrap",
                        backgroundColor:
                          selectedAmount === "15000" ? "#09AACC" : "transparent", // Highlight color
                      }}
                      onClick={() => handlePredefinedAmountClick("15000")}
                    >
                      ₹15,000
                    </button>



                  </div>
                  <div className="col-6 d-flex justify-content-center mb-2">
                    <input
                      type="text"
                      className="Amount w-100"
                      placeholder="Enter amount"
                      aria-label="Amount"
                      style={{
                        borderBottom: "1px solid #e0e0e0",
                        borderTop: "none",
                        borderLeft: "none",
                        borderRight: "none",
                        borderRadius: "0",
                        padding: "0.375rem 0rem",
                        outline: 'none'
                      }}
                      {...register("donationAmount", {
                        required: "Donation amount is required",
                        min: {
                          value: 1,
                          message: "Donation amount must be greater than 0"
                        }
                      })}
                      onChange={(e) => {
                        setValue("donationAmount", e.target.value);
                        setSelectedAmount("");
                      }}
                    />

                  </div>
                  {errors.donationAmount && (
                    <div className="col-12">
                      <span className="error-text">{errors.donationAmount.message}</span>
                    </div>
                  )}
                </div>



                <h3>Your personal details</h3>
                <div className="row mb-3 mt-3">
                  <div className="col-md-6">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="First name"
                      aria-label="First name"
                      {...register("firstName", {
                        required: "First name is required",
                        minLength: {
                          value: 2,
                          message: "First name must be at least 2 characters"
                        }
                      })}
                    />
                    {errors.firstName && (
                      <span className="error-text">{errors.firstName.message}</span>
                    )}
                  </div>
                  <div className="col-md-6 lastName">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Last name"
                      aria-label="Last name"
                      {...register("lastName", {
                        required: "Last name is required",
                        minLength: {
                          value: 2,
                          message: "Last name must be at least 2 characters"
                        }
                      })}
                    />
                    {errors.lastName && (
                      <span className="error-text">{errors.lastName.message}</span>
                    )}
                  </div>
                </div>

                <div className="mb-3">
                  <input
                    type="email"
                    className="form-control"
                    id="inputEmail4"
                    placeholder="Email"
                    {...register("email", {
                      required: "Email is required",
                      pattern: {
                        value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                        message: "Please enter a valid email address"
                      }
                    })}
                  />
                  {errors.email && (
                    <span className="error-text">{errors.email.message}</span>
                  )}
                </div>
                <div className="mb-3">
                  <input
                    type="text"
                    className="form-control"
                    id="inputEmail4"
                    placeholder="Phone"
                    {...register("phone", {
                      required: "Phone is required",
                      pattern: {
                        value: /^[0-9]{10}$/,
                        message: "Phone number must be 10 digits"
                      }
                    })}
                  />
                  {errors.phone && (
                    <span className="error-text">{errors.phone.message}</span>
                  )}
                </div>
                <div className="mb-3">
                  <input
                    type="text"
                    className="form-control"
                    value="India"
                    disabled
                    readOnly
                    style={{
                      backgroundColor: "#f8f9fa",
                      color: "#6c757d",
                      cursor: "not-allowed"
                    }}
                  />
                  <input
                    type="hidden"
                    {...register("country")}
                    value="India"
                  />
                </div>

                <div className="mb-3">
                  <input
                    type="text"
                    className="form-control"
                    id="inputAddress"
                    placeholder="Address Line 1"
                    {...register("address1", {
                      required: "Address Line 1 is required"
                    })}
                  />
                  {errors.address1 && (
                    <span className="error-text">{errors.address1.message}</span>
                  )}
                </div>

                <div className="mb-3">
                  <input
                    type="text"
                    className="form-control"
                    id="inputAddress2"
                    placeholder="Address Line 2"
                    {...register("address2")}
                  />
                  {errors.address2 && (
                    <span className="error-text">{errors.address2.message}</span>
                  )}
                </div>

                <div className="row mb-3">
                  <div className="col-md-6">
                    <input
                      type="text"
                      className="form-control"
                      id="inputCity"
                      placeholder="City"
                      {...register("city", {
                        required: "City is required"
                      })}
                    />
                    {errors.city && (
                      <span className="error-text">{errors.city.message}</span>
                    )}
                  </div>
                  <div className="col-md-6 state">
                    <input
                      type="text"
                      className="form-control"
                      id="inputState"
                      placeholder="State"
                      {...register("state", {
                        required: "State is required"
                      })}
                    />
                    {errors.state && (
                      <span className="error-text">{errors.state.message}</span>
                    )}
                  </div>
                </div>

                <div className="mb-3">
                  <input
                    type="text"
                    className="form-control"
                    id="inputZip"
                    placeholder="Zip"
                    {...register("postalCode", {
                      required: "Postal code is required"
                    })}
                  />
                  {errors.postalCode && (
                    <span className="error-text">{errors.postalCode.message}</span>
                  )}
                </div>

                <div className="mb-3">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="gridCheck"
                      checked={isChecked}
                      onChange={handleCheckboxChange}
                    />
                    <label className="form-check-label" htmlFor="gridCheck">
                      <b>Dedicate this donation</b>
                    </label>
                  </div>
                  {isChecked && (
                    <div>
                      <div className="mb-3 mt-3">
                        <button
                          type="button"
                          className="btn"
                          onClick={() => handleButtonClick("honourOf")}
                          style={{
                            background:
                              selectedOption === "honourOf"
                                ? "#00345B"
                                : "",
                            color: selectedOption === "honourOf" ? "white" : "",
                            borderRadius: "50px",
                            padding: "12px 50px",
                            minWidth: "17%",
                            textAlign: "center",
                            // margin: !isMobile? "2%": 0,
                            borderColor:
                              selectedOption === "honourOf"
                                ? "#00345B"
                                : "#007bff",
                          }}
                        >
                          <b>In Honour Of</b>
                        </button>
                        <button
                          type="button"
                          className="btn"
                          onClick={() => handleButtonClick("memoryOf")}
                          style={{
                            background:
                              selectedOption === "memoryOf"
                                ? "#00345B"
                                : "",
                            color: selectedOption === "memoryOf" ? "white" : "",
                            borderRadius: "50px",
                            padding: "12px 40px",
                            minWidth: "17%",
                            textAlign: "center",
                            marginLeft: !isMobile ? "2%" : 0,
                            borderColor:
                              selectedOption === "memoryOf"
                                ? "#00345B"
                                : "#007bff",
                          }}
                        >
                          <b>In Memory Of</b>
                        </button>
                      </div>

                      {selectedOption === "honourOf" && (
                        <div>
                          <div className="mb-3">
                            <label
                              htmlFor="dedicateeNameHonour"
                              className="form-label"
                            >
                              Dedicatee's Name:
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              id="dedicateeNameHonour"
                              placeholder="Enter name"
                              {...register("honorName", {
                                validate: (value) => {
                                  if (isChecked && !value) {
                                    return "Dedicatee name is required";
                                  }
                                  return true;
                                }
                              })}
                            />
                            {errors.honorName && (
                              <span className="error-text">{errors.honorName.message}</span>
                            )}

                          </div>
                          <div className="mb-3">
                            <label htmlFor="notifyByEmail" className="form-label">
                              Notify by Email:
                            </label>
                            <input
                              type="email"
                              className="form-control"
                              id="notifyByEmail"
                              placeholder="Enter email"
                              {...register("honorEmail", {
                                validate: (value) => {
                                  if (isChecked && selectedOption === "honourOf" && !value) {
                                    return "Email is required for notification";
                                  }
                                  if (value && !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value)) {
                                    return "Please enter a valid email address";
                                  }
                                  return true;
                                }
                              })}
                            />
                            {errors.honorEmail && (
                              <span className="error-text">{errors.honorEmail.message}</span>
                            )}

                          </div>
                        </div>
                      )}

                      {selectedOption === "memoryOf" && (
                        <div>
                          <div className="mb-3">
                            <label
                              htmlFor="dedicateeNameMemory"
                              className="form-label"
                            >
                              Dedicatee's Name:
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              id="dedicateeNameMemory"
                              placeholder="Enter name"
                              {...register("honorName", {
                                validate: (value) => {
                                  if (isChecked && !value) {
                                    return "Dedicatee name is required";
                                  }
                                  return true;
                                }
                              })}
                            />
                            {errors.honorName && (
                              <span className="error-text">{errors.honorName.message}</span>
                            )}

                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="d-grid gap-2">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    style={{
                      background: "#00345B",
                      borderRadius: "40px",
                    }}
                  >
                    Donate
                  </button>
                  {loading && (
                    <div className="loading">Processing your payment...</div>
                  )}
                </div>
              </form>
            ) : (
              // Foreign National - Donation Form
              <ForeignDonationForm />
            )}
          </div>
        </div>
        {citizenType === "indian" ? (
          <img
            className="img_Qe QR-mobile"
            src={upi}
            alt="upi"
            width="60%"
            height="auto"
          />
        ) : (
          <div className="QR-mobile qr-placeholder-mobile">
            <div className="qr-placeholder-icon">
              🚧
            </div>
            <h4 className="qr-placeholder-title">
              Digital Transactions Coming Soon
            </h4>
            <p className="qr-placeholder-text">
              Digital payment options for foreign nationals will be available soon.
              Please use the donation form to proceed with your contribution.
            </p>
          </div>
        )}
      </div>
      <img
        className="mt-5"
        src={ssimg}
        alt="SS"
        //style={{ marginRight:'0%' }}
        width="100%"
        height="700px"
      />
      {/* <div className="image-button-container">
          <h2
            style={{
              textAlign: "center",
              display: "block",
              width: "auto",
              fontSize: "64px",
              fontWeight: "600",
              marginTop: "5%",
              marginBottom: "1%",
              fontFamily: 'Montserrat',

            }}
          >
            <span style={{ color: "#00345B" }}>Ways to </span>
            <span style={{ color: "#00345B" }}>Donate</span>
          </h2>
          <div
            className="image-button-container"
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "60px",
              marginLeft:!isMobile? "3%": 0,
              marginRight:!isMobile? "3%": 0,
            }}
          >
            <ImageButton
              src={credit}
              alt="Credit Card"
              onClick={handleClick}
              type="Credit Card"
              style={{ flex: "1 1 45%", margin: "10px",marginLeft:"20px" }} // Adjust size and spacing
            />
            <ImageButton
              src={debit}
              alt="Debit Card"
              onClick={handleClick}
              type="Debit Card"
              style={{ flex: "1 1 45%", margin: "5px" }} // Adjust size and spacing
            />
            <ImageButton
              src={upiSymbol}
              alt="UPI"
              onClick={handleClick}
              type="UPI"
              style={{ flex: "1 1 45%", margin: "5px" }} // Adjust size and spacing
            />
            <ImageButton
              src={gPay}
              alt="Gpay"
              onClick={handleClick}
              type="Google Pay"
              style={{ flex: "1 1 45%", margin: "5px" }} // Adjust size and spacing
            />
            <ImageButton
              src={netBank}
              alt="netBanking"
              onClick={handleClick}
              type="Net Banking"
              style={{ flex: "1 1 45%", margin: "5px" }} // Adjust size and spacing
            />
          </div>
        </div> */}
      <div
        className="container-fluid"
        style={{
          background: "#00345b",
          color: "rgba(255, 255, 255, 1)",
          fontSize: "24px",
          fontWeight: "500",
          textAlign: "left",
          height: "auto",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          fontFamily: 'Montserrat',
          marginTop: "20px"
          //justifyContent: "center",
        }}
      >
        <div
          className="faqs-header"
          style={{ marginTop: "6%", marginLeft: "8%" }}
        >
          <h2 style={{ fontSize: "48px" }}>FAQs</h2>
        </div>
        <Box
          sx={{
            width: { xs: "95%", sm: "100%", lg: "84%" },
            height: "auto",
            marginTop: "2%",
            marginLeft: {
              xs: "2%",
              sm: "2%",
              lg: "4%",
            },
            padding: { xs: "1rem", sm: "2rem", lg: "2rem" }, // Add padding for spacing
            borderRadius: "8px", // Add border-radius for rounded corners
          }}
        >
          {faqData.map((item, index) => (
            <Box
              key={index}
              sx={{
                padding: "1rem",
                borderRadius: "8px",
                marginBottom: "1rem",
              }}
            >
              <Typography
                onClick={() => handleToggle(index)}
                sx={{
                  fontSize: { xs: "14px", md: "16px", lg: "24px" },
                  fontWeight: 500,
                  cursor: "pointer",
                  color: "white",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  fontFamily: 'Source Sans Pro',

                }}
              >
                {item.question}
                <i className="arrow down" style={{ fontSize: "16px" }}></i> {/* Adjust arrow size */}
              </Typography>

              <Collapse in={expanded[index]}>
                <Box
                  sx={{
                    marginTop: "1rem",
                    fontSize: { xs: "14px", sm: "16px", lg: "18px" },
                    color: "white",
                    fontFamily: 'Source Sans Pro',
                  }}
                >
                  {/* Dropdown content goes here */}
                  {item.answer}

                </Box>
              </Collapse>
            </Box>
          ))}
        </Box>

      </div>
      {!isMobile ? <img
        className="quote"
        src={slogan}
        alt="slogan"
        //style={{ borderRadius: "50%" }}
        width="100%"
        height="473px"
      /> :
        <img
          className="quote"
          src={sloganMob}
          alt="slogan"
          //style={{ borderRadius: "50%" }}
          width="100%"
          height="auto"
        />
      }
      <DonationStatusModal
        open={statusModal.open}
        type={statusModal.type}
        title={statusModal.title}
        message={statusModal.message}
        details={statusModal.details}
        primaryButtonText={statusModal.primaryButtonText}
        onPrimaryAction={statusModal.onPrimaryAction}
        onClose={closeStatusModal}
      />
    </Fragment>
  );
};

export default DonateForm;